import { RecentArticles } from "@/components/dashboard/RecentArticles";
import { RecommendedSchedule } from "@/components/dashboard/RecommendedSchedule";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useBrand } from "@/contexts/brand-context";

import { pb } from "@/lib/pocketbase";
import { ContentSchedules, Posts } from "@/types/pocketbase-types";
import {
  faArrowRight,
  faPenToSquare,
  faPlus,
  faFileLines,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface PostsStats {
  id: string;
  org: string;
  total_articles: number;
  published: number;
  draft: number;
  scheduled: number;
}

export default function Dashboard() {
  const { currentBrand } = useBrand();
  const [contentSchedules, setContentSchedules] = useState<ContentSchedules[]>(
    [],
  );
  const [recentArticles, setRecentArticles] = useState<
    {
      id: string;
      title: string;
      status: "published" | "draft" | "scheduled";
      date: string;
      views: number;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    scheduled: 0,
  });

  // Fetch content schedules from PocketBase
  useEffect(() => {
    const fetchContentSchedules = async () => {
      if (!currentBrand) return;

      try {
        const records = await pb
          .collection("content_schedules")
          .getList<ContentSchedules>(1, 5, {
            filter: `brand = "${currentBrand.id}" && status = "scheduled" && org="${currentBrand.org}"`,
            sort: "+scheduleDate",
            expand: "post",
          });

        setContentSchedules(records.items);
      } catch (error) {
        console.error("Error fetching content schedules:", error);
      }
    };

    fetchContentSchedules();
  }, [currentBrand]);

  // Reset hasFetched when brand changes to trigger fresh fetch
  useEffect(() => {
    setHasFetched(false);
    setIsLoading(true);
  }, [currentBrand?.id]);

  // Fetch dashboard data when brand is available and we haven't fetched yet
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentBrand) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch recent articles
        const records = await pb.collection("posts").getList<Posts>(1, 4, {
          filter: `brand = "${currentBrand.id}"`,
          sort: "-created",
        });

        // Transform Posts to the format expected by RecentArticles component
        const articles = records.items.map((post) => ({
          id: post.id,
          title: post.title,
          status: post.status.toLowerCase() as
            | "published"
            | "draft"
            | "scheduled",
          date: format(new Date(post.created), "yyyy-MM-dd"),
          views: 0, // TODO: Implement view tracking or analytics integration
        }));

        setRecentArticles(articles);

        // Fetch stats
        try {
          const stats = await pb
            .collection("posts_stats")
            .getOne<PostsStats>(currentBrand?.id);
          setStats({
            total: stats.total_articles,
            published: stats.published,
            draft: stats.draft,
            scheduled: stats.scheduled,
          });
        } catch (error) {
          console.error("Error fetching article stats:", error);
        }

        setHasFetched(true);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setHasFetched(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have a brand and haven't fetched for this brand yet
    if (currentBrand && !hasFetched) {
      fetchDashboardData();
    }
  }, [currentBrand, hasFetched]);
  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold tracking-tight ai-gradient-text">
          Welcome back!
        </h1>
        <p className="text-muted-foreground text-lg">
          Here's what's happening with your content today
        </p>
      </div>

      {/* Article Stats */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Content Analytics</CardTitle>
          <CardDescription>
            Overview of your content performance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="h-6 w-6 text-primary"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Articles
                </p>
                <h3 className="text-2xl font-bold">{stats.total}</h3>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="h-6 w-6 text-green-700"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Published
                </p>
                <h3 className="text-2xl font-bold">{stats.published}</h3>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="h-6 w-6 text-gray-700"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Drafts
                </p>
                <h3 className="text-2xl font-bold">{stats.draft}</h3>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <FontAwesomeIcon
                  icon={faClock}
                  className="h-6 w-6 text-yellow-700"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Scheduled
                </p>
                <h3 className="text-2xl font-bold">{stats.scheduled}</h3>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommended Schedule and Recent Articles - One Row, Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Both cards will be wrapped in identical height containers */}
        <div className="h-[400px]">
          {isLoading ? (
            <Card className="h-full flex flex-col">
              <CardHeader>
                <CardTitle>Content Pipeline</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">Loading content pipeline...</div>
              </CardContent>
            </Card>
          ) : (
            <div className="h-full">
              <RecommendedSchedule
                schedules={contentSchedules.map((schedule) => ({
                  id: schedule.id,
                  date: new Date(schedule.scheduleDate),
                  suggestedTopic: schedule.topic,
                }))}
              />
            </div>
          )}
        </div>
        <div className="h-[400px]">
          {isLoading ? (
            <Card className="h-full flex flex-col">
              <CardHeader>
                <CardTitle>Recent Articles</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">Loading recent articles...</div>
              </CardContent>
            </Card>
          ) : recentArticles.length > 0 ? (
            <div className="h-full">
              <RecentArticles items={recentArticles} />
            </div>
          ) : (
            <Card className="h-full flex flex-col overflow-hidden">
              <CardHeader>
                <CardTitle>Recent Articles</CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex-1">
                <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-t">
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faPenToSquare}
                        className="h-10 w-10 text-primary animate-pulse"
                      />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center animate-bounce">
                      <FontAwesomeIcon
                        icon={faPlus}
                        className="h-4 w-4 text-white"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    No articles yet
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Create your first article to start building your content
                    library.
                  </p>
                  <Button asChild className="group">
                    <Link to="/articles/new">
                      Create your first article
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 h-4 w-4 transform transition-transform group-hover:translate-x-1"
                      />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
